import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { UserData } from '../../data-models/user-models/user-data.model';
import { VesselData } from '../../data-models/user-models/vessel-data.model';

@Component({
    selector: 'app-add-vessel-dialog',
    templateUrl: './add-vessel-dialog.component.html',
    styleUrls: ['./add-vessel-dialog.component.css'],
    standalone: false
})
export class AddVesselDialogComponent implements OnInit{

  userDetails: UserData = new UserData();
  duplicateVesselFlag: boolean = false; // used if the VRN entered is not unique
  editVessel?: VesselData; // the vessel being edited; undefined when adding a new vessel
  dialogMode: string = 'add'; // 'add' or 'edit' - drives the ICU select translations in the template
  vrnLocked: boolean = false; // VRN can't change while a registered logbook depends on it

  vesselForm: UntypedFormGroup = new UntypedFormGroup({
    vesselName: new FormControl<string>('', [Validators.required]),
    vesselNumber: new FormControl<string>('', [Validators.required])
  });

  constructor(
    private dialogRef: MatDialogRef<AddVesselDialogComponent>,
    public sharedMethodsService: SharedMethodsService,
    @Inject(MAT_DIALOG_DATA) private data: { userDetails: UserData, vessel?: VesselData }
  ) {
    this.userDetails = this.data.userDetails;
    this.editVessel = this.data.vessel;
    this.dialogMode = this.editVessel ? 'edit' : 'add';
  }


  /*  ---------- Initialization Methods  ---------- */


  ngOnInit(): void {

    if (this.editVessel) {
      this.vesselForm.patchValue({
        vesselName: this.editVessel.vesselName,
        vesselNumber: this.editVessel.vesselRegistrationNumber
      });
      this.vrnLocked = this.userDetails.logbooks.some((logbook) =>
        logbook.isRegistered && logbook.vessel.vesselRegistrationNumber === this.editVessel!.vesselRegistrationNumber);
      if (this.vrnLocked) {
        this.vesselForm.get('vesselNumber')?.disable();
      }
    }

  }



  // checks to see if currLogbook is a duplicate of one of the user's pre-existing logbooks
  isVesselUnique(): boolean {

    let currVRN: string = this.vesselForm.get('vesselNumber')?.value.trim();
    let userVessels: VesselData[] = this.userDetails.vessels;

    for (let vessel of userVessels) {
      if (vessel === this.editVessel) {
        continue; // don't compare the vessel being edited against itself
      }
      if (currVRN === vessel.vesselRegistrationNumber) {
        return false; // return false if a duplicate is found
      }
    }
    return true; // return true if currLogbook is unique

  }


  checkForDuplicateVessel(): boolean {

    if (this.isVesselUnique()) {
      return false;
    }
    this.sharedMethodsService.toastVRNUniqueError();
    return true;

  }


  /*  ---------- Dialog Close Methods  ---------- */


  // invoked when the user presses the 'add'/'save' button; saves the vessel to user storage
  addVessel(): void {

    this.duplicateVesselFlag = false;

    if (this.vesselForm.invalid) {
      this.vesselForm.markAllAsTouched();
      return;
    }

    this.duplicateVesselFlag = this.checkForDuplicateVessel();
    if (!this.duplicateVesselFlag) {
      this.saveData(); // save logbook data to user storage
      UserStorageService.saveUser(this.userDetails).then(() => {
        console.log(this.userDetails);
        this.closeDialog();
      });
    }

  }

  // assigns the vessel data from the dialog form to the user's data
  saveData(): void {

    let vessel: VesselData;
    if (this.editVessel) {
      vessel = this.editVessel; // update the existing vessel in place
    } else {
      vessel = new VesselData();
      this.userDetails.vessels.push(vessel); // add the vessel to the list of user vessels
    }
    vessel.vesselName = this.vesselForm.get('vesselName')?.value.trim();
    vessel.vesselRegistrationNumber = this.vesselForm.get('vesselNumber')?.value.trim();

    // Update any logbooks to become registered if they have a VRN matching this vessel's VRN
    for (let logbook of this.userDetails.logbooks.filter(logbook => logbook.vessel.vesselRegistrationNumber == vessel.vesselRegistrationNumber)) {
      logbook.isRegistered = true;
      logbook.vessel = vessel;
    }

  }

  exitDialog(): void {

    this.dialogRef.close('Back');

  }

  // closes the dialog with the appropriate flag
  closeDialog(): void {

    // close the dialog
    this.dialogRef.close(this.editVessel ? 'edit' : 'add');

  }

}
